import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/settings" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-4">
            <ArrowLeft size={16} />
            Back to Settings
          </Link>
          <div className="flex items-center gap-3">
            <Cookie size={28} className="text-gold" />
            <h1 className="font-serif text-3xl text-white">Cookie Policy</h1>
          </div>
          <p className="text-muted-foreground mt-2">Last updated: March 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-invert prose-gold max-w-none space-y-8">
          
          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">1. What Are Cookies?</h2>
            <p className="text-gray-400 leading-relaxed">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit 
              a website. They are widely used to make websites work more efficiently and to provide information to the 
              owners of the site. Cookies help us remember your preferences and improve your experience on Assagart.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">2. How We Use Cookies</h2>
            <p className="text-gray-400 leading-relaxed">
              Assagart uses cookies for the following purposes:
            </p>
            <ul className="text-gray-400 list-disc list-inside space-y-2 mt-4">
              <li><strong className="text-white">Authentication:</strong> To keep you signed in and secure your session</li>
              <li><strong className="text-white">Preferences:</strong> To remember your settings such as currency and theme</li>
              <li><strong className="text-white">Security:</strong> To protect your account and detect suspicious activity</li>
              <li><strong className="text-white">Analytics:</strong> To understand how you use our platform and improve our services</li>
              <li><strong className="text-white">Performance:</strong> To ensure the platform loads quickly and functions properly</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">3. Types of Cookies We Use</h2>
            
            <div className="space-y-6 mt-4">
              <div className="p-4 bg-[#111111] border border-[#262626]">
                <h3 className="text-white font-medium mb-2">Essential Cookies</h3>
                <p className="text-gray-400 text-sm">
                  These cookies are necessary for the website to function and cannot be switched off. They are usually 
                  set in response to actions you take, such as logging in or setting your privacy preferences.
                </p>
                <p className="text-gold text-xs mt-2">Duration: Session / 24 hours</p>
              </div>

              <div className="p-4 bg-[#111111] border border-[#262626]">
                <h3 className="text-white font-medium mb-2">Functional Cookies</h3>
                <p className="text-gray-400 text-sm">
                  These cookies enable enhanced functionality and personalisation, such as remembering your preferred 
                  currency (GBP, USD, EUR, CHF) and display settings.
                </p>
                <p className="text-gold text-xs mt-2">Duration: 1 year</p>
              </div>

              <div className="p-4 bg-[#111111] border border-[#262626]">
                <h3 className="text-white font-medium mb-2">Analytics Cookies</h3>
                <p className="text-gray-400 text-sm">
                  These cookies help us understand how visitors interact with our platform by collecting and reporting 
                  information anonymously. This helps us improve our services.
                </p>
                <p className="text-gold text-xs mt-2">Duration: 2 years</p>
              </div>

              <div className="p-4 bg-[#111111] border border-[#262626]">
                <h3 className="text-white font-medium mb-2">Security Cookies</h3>
                <p className="text-gray-400 text-sm">
                  These cookies help protect your account by detecting unusual login attempts and verifying your 
                  identity during sensitive operations.
                </p>
                <p className="text-gold text-xs mt-2">Duration: Session</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">4. Third-Party Cookies</h2>
            <p className="text-gray-400 leading-relaxed">
              Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:
            </p>
            <ul className="text-gray-400 list-disc list-inside space-y-2 mt-4">
              <li><strong className="text-white">Stripe:</strong> For secure payment processing</li>
              <li><strong className="text-white">Analytics providers:</strong> For understanding platform usage</li>
            </ul>
            <p className="text-gray-400 leading-relaxed mt-4">
              These third parties have their own privacy policies and we encourage you to read them.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">5. Managing Cookies</h2>
            <p className="text-gray-400 leading-relaxed">
              You can control and/or delete cookies as you wish. You can delete all cookies that are already on your 
              device and you can set most browsers to prevent them from being placed. However, if you do this, you may 
              have to manually adjust some preferences every time you visit our platform, and some features may not work.
            </p>
            <div className="mt-4 p-4 bg-[#111111] border border-[#262626]">
              <p className="text-white font-medium mb-2">How to manage cookies in your browser:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>Chrome: Settings → Privacy and Security → Cookies</li>
                <li>Firefox: Options → Privacy & Security → Cookies</li>
                <li>Safari: Preferences → Privacy → Cookies</li>
                <li>Edge: Settings → Privacy → Cookies</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">6. Local Storage</h2>
            <p className="text-gray-400 leading-relaxed">
              In addition to cookies, we use local storage to store your authentication token and certain preferences. 
              Local storage is similar to cookies but allows for larger amounts of data to be stored. You can clear 
              local storage through your browser's developer tools or settings.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">7. Updates to This Policy</h2>
            <p className="text-gray-400 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other 
              operational, legal, or regulatory reasons. We will notify you of any material changes by posting the 
              updated policy on this page with a new "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">8. Contact Us</h2>
            <p className="text-gray-400 leading-relaxed">
              If you have any questions about our use of cookies, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-[#111111] border border-[#262626]">
              <p className="text-white">Assagart Limited</p>
              <p className="text-gray-400">Email: assagart.contact@gmail.com</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#1A1A1A] py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-muted-foreground text-sm">© 2026 Assagart. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
